import { useState, useEffect, useRef } from "react";
import { api, formatApiError, API_BASE } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

const NEW_BANK = { bank: "", account_number: "", account_name: "" };

export default function CompanySettingsDialog({ onClose }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef(null);

  useEffect(() => {
    api.get("/settings/company").then(({ data }) => setForm({ bank_accounts: [], ...data })).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const addBank = () => setForm((f) => ({ ...f, bank_accounts: [...(f.bank_accounts || []), { ...NEW_BANK }] }));
  const updBank = (i, k, v) => setForm((f) => ({ ...f, bank_accounts: f.bank_accounts.map((b, j) => (j === i ? { ...b, [k]: v } : b)) }));
  const delBank = (i) => setForm((f) => ({ ...f, bank_accounts: f.bank_accounts.filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", form);
      setForm({ bank_accounts: [], ...data });
      toast.success("Company settings saved");
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/settings/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ bank_accounts: [], ...prev, ...data }));
      toast.success("Logo updated");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const inputCls = "w-full h-9 px-3 text-sm rounded-md border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg bg-background max-h-[90vh] overflow-y-auto" data-testid="company-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Company settings</DialogTitle>
          <DialogDescription>Shown in the header of generated SPK, Invoice & Kuitansi documents.</DialogDescription>
        </DialogHeader>
        {form === null ? (
          <div className="flex justify-center py-8"><div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : (
          <div className="space-y-3 py-1">
            <div className="flex items-center gap-3">
              {form.logo_path ? (
                <img src={`${API_BASE}/files/${form.logo_path}`} alt="Company logo" className="h-12 rounded border border-border bg-white p-1" data-testid="company-logo-preview" />
              ) : (
                <div className="h-12 w-12 rounded border border-dashed border-border flex items-center justify-center text-muted-foreground text-[10px]">No logo</div>
              )}
              <button type="button" data-testid="company-logo-upload" onClick={() => logoRef.current?.click()}
                className="h-8 px-3 flex items-center gap-1.5 text-xs rounded-md border border-border hover:bg-muted transition-colors">
                <Upload size={13} />Upload logo
              </button>
              <input type="file" accept="image/*" ref={logoRef} className="hidden" data-testid="company-logo-input" onChange={uploadLogo} />
            </div>
            {[["name", "Company name"], ["slogan", "Slogan (letterhead)"], ["address", "Address"], ["phone", "Phone"], ["email", "Email"]].map(([k, label]) => (
              <div className="space-y-1.5" key={k}>
                <label className="text-xs font-medium text-muted-foreground">{label}</label>
                <input value={form[k] || ""} onChange={set(k)} data-testid={`company-${k}-input`} className={inputCls} />
              </div>
            ))}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Signatory name</label>
                <input value={form.signatory_name || ""} onChange={set("signatory_name")} data-testid="company-signatory-name-input" className={inputCls} placeholder="Ir. Yusuf Alima Wiyoga" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Signatory title</label>
                <input value={form.signatory_title || ""} onChange={set("signatory_title")} data-testid="company-signatory-title-input" className={inputCls} placeholder="Direktur Utama" />
              </div>
            </div>
            <div className="space-y-2" data-testid="company-bank-section">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-muted-foreground">Bank accounts (for Invoice & Kuitansi)</label>
                <button type="button" data-testid="company-bank-add" onClick={addBank}
                  className="h-6 px-2 flex items-center gap-1 text-[11px] rounded-md border border-dashed border-border hover:bg-muted transition-colors">
                  <Plus size={11} />Tambah Rekening
                </button>
              </div>
              {(form.bank_accounts || []).map((b, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.4fr_1.2fr_auto] gap-1.5 items-center" data-testid={`company-bank-row-${i}`}>
                  <input value={b.bank} onChange={(e) => updBank(i, "bank", e.target.value)} placeholder="Bank (BRI/BCA)" data-testid={`company-bank-bank-${i}`} className={inputCls} />
                  <input value={b.account_number} onChange={(e) => updBank(i, "account_number", e.target.value)} placeholder="Nomor rekening" data-testid={`company-bank-number-${i}`} className={inputCls} />
                  <input value={b.account_name} onChange={(e) => updBank(i, "account_name", e.target.value)} placeholder="Atas nama" data-testid={`company-bank-name-${i}`} className={inputCls} />
                  <button type="button" data-testid={`company-bank-del-${i}`} onClick={() => delBank(i)}
                    className="h-9 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-[#E31E24] hover:bg-[#E31E24]/10 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <button data-testid="company-settings-cancel" onClick={onClose} className="h-8 px-3 text-sm rounded-md border border-border hover:bg-muted transition-colors">Cancel</button>
          <button data-testid="company-settings-save" onClick={save} disabled={saving || !form}
            className="h-8 px-4 text-sm rounded-md bg-[#E31E24] hover:bg-[#C81A1F] text-white font-medium transition-colors disabled:opacity-60 flex items-center gap-1.5">
            {saving && <Loader2 size={13} className="animate-spin" />}Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
